import { useAuth } from '@/features/auth/context/auth-context';
import { useAccess } from '@/features/access/access';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query/query-keys';
import { requirementImportsApi } from '../api/requirement-imports.api';
import type { RequirementUploadResult } from '../types';
/** The persistent history remains accessible after navigation or refresh. */
export const useUploadRequirements = (): UseMutationResult<
  RequirementUploadResult,
  ApiError,
  File[]
> => {
  const client = useQueryClient();
  return useMutation({
    mutationKey: [...queryKeys.requirementImports.all, 'upload'],
    mutationFn: (files: File[]) => requirementImportsApi.upload(files),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.requirementImports.all });
    },
  });
};

export const useRequirementImportSummary = () => {
  const { user } = useAuth();
  const access = useAccess();
  return useQuery({
    queryKey: [...queryKeys.requirementImports.all, 'summary', user?.id],
    queryFn: requirementImportsApi.summary,
    enabled: !!user && access.can('requirements', 'read'),
    staleTime: 2000,
    refetchInterval: (query) =>
      query.state.data && query.state.data.counts.PENDING + query.state.data.counts.PROCESSING > 0
        ? 3000
        : 15000,
  });
};
